/**
 * The reading pane while a real mail folder is open.
 *
 * Read-only on purpose, and it says so: there is no reply, no archive and no
 * delete here. Filing and deleting are the list's right-click menu, which acts
 * on the mail server by name and says what it did (`ImapMessageItems`). A
 * reply is nowhere: it would start a CRM thread with no link back to this
 * message, which is a copy of what the server holds this second - and a
 * button that quietly did that would be the worst of both.
 *
 * The message is untrusted sender HTML and goes through `MailBody` with the
 * same sanitising the inbox uses. Nothing about being read live makes it safer.
 */
import { Loader2, Mail } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { imapAttachmentUrl, type ImapMessageBody } from "../../../../lib/mail";
import { Avatar, fmtBytes, fmtDateTime } from "../../../ui";
import { PaneBack } from "../../_shared/workMobile";
import { AttachmentChips } from "../../_shared/attachments";
import { MailBody } from "../MailBody";
import { imapReasonText } from "./imapFolderErrors";

export function ImapMessagePane({
  message,
  loading,
  reason,
  picked,
  path,
  uidValidity,
  mailbox,
  onBack,
}: {
  message: ImapMessageBody | null;
  loading: boolean;
  reason: string | null;
  /** Whether a message has been picked at all, which is not the same as having one. */
  picked: boolean;
  /** The open folder. Needed to fetch a file: nothing here is stored, so a
   *  download names the message on the mail server rather than a row of ours. */
  path: string;
  /** Quoted back on a download, the same way the read quotes it. */
  uidValidity: string;
  /** The mailbox the folder is in. Absent: the shared one. */
  mailbox?: string;
  /** Back to the folder's message list. A phone shows one pane at a time. */
  onBack: () => void;
}) {
  const t = useT();

  if (loading) {
    return (
      <div className="crm-card crm-work-col items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (reason) {
    return (
      <div className="crm-card crm-work-col">
        <div className="crm-pane-none">
          <Mail className="size-8 opacity-50" />
          <span className="max-w-[46ch] text-center">{imapReasonText(t, reason, "email.imap.browse.readFailed")}</span>
        </div>
      </div>
    );
  }

  if (!message) {
    return (
      <div className="crm-card crm-work-col">
        <div className="crm-pane-none">
          <Mail className="size-8 opacity-50" />
          <span>{t(picked ? "email.imap.browse.gone" : "email.imap.browse.pickOne")}</span>
        </div>
      </div>
    );
  }

  const who = message.fromName || message.fromAddress || t("email.imap.browse.noSender");

  return (
    <div className="crm-card crm-work-col">
      <div className="crm-read-head">
        <div className="crm-read-top flex items-start gap-3">
          <PaneBack label={t("common.back")} onBack={onBack} />
          <div className="crm-read-title crm-read-subject">
            {message.subject || t("email.imap.browse.noSubject")}
          </div>
        </div>

        <div className="crm-read-meta">
          <Avatar name={who} size={40} />
          <span className="min-w-0 flex-1">
            <span className="block text-[13.5px] font-[650]">{who}</span>
            <span className="crm-mut mt-0.5 block text-[11.75px]">
              {message.fromAddress || "-"} &middot; {fmtDateTime(message.date)} &middot; {fmtBytes(message.size)}
            </span>
            {message.to.length ? (
              <span className="crm-mut crm-trunc mt-0.5 block text-[11.75px]">
                {t("email.to")}: {message.to.join(", ")}
                {message.cc.length ? ` · ${t("email.cc")}: ${message.cc.join(", ")}` : ""}
              </span>
            ) : null}
          </span>
        </div>

        {/* One quiet line, because the missing buttons need explaining. */}
        <p className="crm-mut mt-3 text-[11.25px]">{t("email.imap.browse.readOnly")}</p>
      </div>

      <div className="crm-read-body">
        {message.tooLarge ? (
          <p className="crm-pane-empty">{t("email.imap.browse.tooLarge", { size: fmtBytes(message.size) })}</p>
        ) : (
          <MailBody t={t} html={message.html} text={message.text} />
        )}

        {/* Drawn even when the body was refused: a message too big to render is
            usually one whose weight IS the file, and that is exactly the file
            the operator opened it for. The list comes from BODYSTRUCTURE, so it
            survives a message that was never fetched. */}
        <AttachmentChips
          t={t}
          items={message.attachments}
          className="mt-3"
          href={(i) => imapAttachmentUrl(path, message.uid, i, uidValidity || undefined, mailbox)}
        />
      </div>
    </div>
  );
}
